// The toast of a provider notice. A login_required notice names the provider whose turn was
// refused (the daemon sends the one that RAN, never the stale binding's) and the machine it ran
// on, and its button is the failure card's own "Sign in again" (turn-error-signin.ts).
import { goto } from "$app/navigation";
import { toast } from "svelte-sonner";
import { PROVIDER_LABELS } from "$lib/agent-display.js";
import { daemonState } from "$lib/plugins/agents/state.svelte.js";
import { isMe } from "$lib/session-scope.js";
import { authState } from "../core/state.svelte.js";

export interface NoticeForToast {
  agentId: string;
  workspaceId: string;
  kind: string;
  level: "info" | "warning";
  text: string;
  provider?: string;
  daemonId?: string;
  authFailure?: string; // "keychain_unavailable": the remedy is a restart, not a sign-in
}

interface Bridge {
  restartDaemon?: () => Promise<unknown>;
}

// `who`: the agent's display name; `rowDaemonId`: its session row's machine (older daemons send none).
export function showNoticeToast(
  notice: NoticeForToast,
  who: string | undefined,
  rowDaemonId: string | null | undefined,
): void {
  const show = notice.level === "warning" ? toast.warning : toast.info;
  const said = (text: string) => (who ? `${who}: ${text}` : text);
  const { provider } = notice;
  if (notice.kind !== "login_required" || !provider) return void show(said(notice.text));
  const daemonId = notice.daemonId ?? rowDaemonId ?? undefined;
  const machine = (daemonId && daemonState.byId(daemonId)?.label) || "this computer";
  // The background service lost the keychain (a desktop logout): only a restart gives it back.
  const restart = (globalThis as { cyborg7Desktop?: Bridge }).cyborg7Desktop?.restartDaemon;
  if (notice.authFailure === "keychain_unavailable") {
    const lost = said(`Cyborg's background service on ${machine} lost access to its keychain.`);
    // The bridge restarts the daemon it spawned: only when that IS the notice's machine, and ours
    // (ProviderAuthStrip's gate). Else a click would kill this computer's turns and fix nothing.
    const owns = !!daemonId && isMe(daemonState.byId(daemonId)?.ownerId, authState.user);
    const here = owns && daemonState.connectedDaemonId === daemonId;
    if (!restart || !here) return void show(`${lost} Restart Cyborg there.`);
    const label = "Restart Cyborg's background service";
    return void show(lost, { action: { label, onClick: () => void restart() } });
  }
  const text = said(`Sign in to ${PROVIDER_LABELS[provider] ?? provider} again on ${machine}.`);
  if (!daemonId) return void show(text);
  const target = { workspaceId: notice.workspaceId, agentId: notice.agentId, daemonId, provider };
  // Until the in-chat sign-in lands, the card's fallback here is that conversation (its card).
  const openConversation = () =>
    void goto(`/workspace/${target.workspaceId}/agent/${target.agentId}`);
  // Loaded on click: turn-error-signin reaches app.svelte.js, which imports this module.
  const onClick = () =>
    void import("$lib/plugins/agents/turn-error-signin.js").then(
      ({ signInAgain }) => signInAgain(target, openConversation),
      openConversation,
    );
  show(text, { action: { label: "Sign in again", onClick } });
}
